import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import './App.css';
import SearchBox from './components/SearchBox';
import SearchResults from './components/SearchResults';
import LoadingSpinner from './components/LoadingSpinner';
import FilterPanel from './components/FilterPanel';
import DocumentViewer from './components/DocumentViewer';
import Modal from './components/Modal';
import { selectAppliedFilters, selectDateFilters, selectAvailableFilters } from './store/filterSlice';
import { buildSolrFilterQuery, buildDateRangeQuery } from './services/filterService';

function App() {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [documentViewer, setDocumentViewer] = useState({
    isOpen: false,
    documentUrl: null,
    pageNumber: 1
  });

  const appliedFilters = useSelector(selectAppliedFilters);
  const dateFilters = useSelector(selectDateFilters);
  const availableFilters = useSelector(selectAvailableFilters);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);

  useEffect(() => {
    if (searchQuery && results) {
      const timeoutId = setTimeout(() => {
        performSearch(searchQuery);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [appliedFilters, dateFilters]);

  const performSearch = async (searchText) => {
    if (!searchText.trim()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let filterQuery = buildSolrFilterQuery(appliedFilters, availableFilters);

      Object.entries(dateFilters).forEach(([fieldName, dateRange]) => {
        // For date filters, also need to map to correct field name
        const filterConfig = availableFilters.find(f => f.key === fieldName);
        const solrFieldName = filterConfig?.srch_field || fieldName;
        filterQuery += buildDateRangeQuery(solrFieldName, dateRange.startDate, dateRange.endDate);
      });

      const response = await fetch('/api/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          search_query: searchText,
          selected_filters: appliedFilters,
          date_filters: dateFilters,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Transform API response to match frontend expectations
        const transformedResults = {
          success: true,
          documents: data.data.results,
          total: data.data.total,
          query: data.data.query_info?.original_query || searchText,
          facets: data.data.facets || {}
        };
        setResults(transformedResults);
      } else {
        setError(data.error || 'An error occurred while searching');
      }
    } catch (err) {
      setError('Failed to connect to the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (searchText) => {
    setSearchQuery(searchText);
    setResults(null);
    await performSearch(searchText);
  };

  const handleDownloadExcel = async () => {
    if (!results || !results.documents) return;

    try {
      const response = await fetch('/api/export-excel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          documents: results.documents,
          query: searchQuery,
          filters: appliedFilters,
          date_filters: dateFilters,
        }),
      });

      if (response.ok) {
        // Create a blob from the response
        const blob = await response.blob();

        // Create a URL for the blob
        const url = window.URL.createObjectURL(blob);

        // Create a temporary anchor element to trigger download
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;

        // Get filename from response headers or use default
        const contentDisposition = response.headers.get('content-disposition');
        let filename = 'search_results.xlsx';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }

        a.download = filename;
        document.body.appendChild(a);
        a.click();

        // Clean up
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Failed to download Excel file');
      }
    } catch (error) {
      console.error('Error downloading Excel file:', error);
    }
  };

  const handleReset = () => {
    setSearchQuery('');
    setResults(null);
    setError(null);
    setDocumentViewer({ isOpen: false, documentUrl: null, pageNumber: 1 });
  };

  const handleOpenDocument = (url, page = 1) => {
    setDocumentViewer({
      isOpen: true,
      documentUrl: url,
      pageNumber: page
    });
  };

  const handleCloseDocument = () => {
    setDocumentViewer({ isOpen: false, documentUrl: null, pageNumber: 1 });
  };

  return (
    <div className={`App ${darkMode ? 'dark' : ''}`}>
      <header className={`App-header ${scrolled ? 'scrolled' : ''}`}>
        <div className="header-container">
          <div className="brand">
            <div className="logo">
              <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="40" height="40" rx="8" fill="url(#gradient)"/>
                <path d="M12 20L18 14L24 20L30 14" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 26L18 20L24 26L30 20" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                <defs>
                  <linearGradient id="gradient" x1="0" y1="0" x2="40" y2="40">
                    <stop stopColor="#667eea"/>
                    <stop offset="1" stopColor="#764ba2"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <div className="brand-text">
              <h1>IP Author</h1>
            </div>
          </div>
          <nav className="nav-menu">
            <button
              className="theme-toggle"
              onClick={() => setDarkMode(!darkMode)}
              aria-label="Toggle dark mode"
            >
              {darkMode ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="12" cy="12" r="5"/>
                  <path d="M12 1v6M12 17v6M4.22 4.22l4.24 4.24M15.54 15.54l4.24 4.24M1 12h6M17 12h6M4.22 19.78l4.24-4.24M15.54 8.46l4.24-4.24"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
              )}
            </button>
          </nav>
        </div>
      </header>

      <main className="App-main">
        {!searchQuery ? (
          <div className="home-view">
            <div className="hero-section">
              <div className="hero-background">
                <div className="gradient-sphere sphere-1"></div>
                <div className="gradient-sphere sphere-2"></div>
                <div className="gradient-sphere sphere-3"></div>
              </div>
              <div className="hero-content">
                <h2 className="hero-title">
                  <span className="gradient-text">Standards Insight</span>
                </h2>
                <p className="hero-subtitle">
                  Map patent claims to prior art or ask technical questions against Multimedia, 3GPP or IEEE standards.
                </p>
                <div className="hero-search">
                  <SearchBox onSearch={handleSearch} isHome={true} />
                </div>
              </div>
            </div>

            <div className="home-content">
              <div className="filters-sidebar">
                <FilterPanel />
              </div>
              <div className="main-content">
                <div className="home-description">
                  <h3>Advanced Search Filters</h3>
                  <p>Use the detailed filters on the left to refine your search before you begin. You can filter by document type, author, status, date ranges, and more.</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="results-view">
            <div className="results-header">
              <button className="back-button" onClick={handleReset}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
                Back to Search
              </button>
              <div className="search-bar-container">
                <SearchBox
                  onSearch={handleSearch}
                  initialValue={searchQuery}
                  isHome={false}
                />
              </div>
            </div>

            <div className="results-content">
              <div className="filters-sidebar">
                <FilterPanel isOnResultPage={true} />
              </div>

              <div className="main-content">
                {loading && (
                  <div className="loading-container">
                    <LoadingSpinner />
                    <p className="loading-text">Searching documents...</p>
                    <div className="loading-progress">
                      <div className="progress-bar"></div>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="error-container">
                    <div className="error-card">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" className="error-icon">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                      </svg>
                      <p className="error-text">{error}</p>
                      <button className="retry-button" onClick={() => handleSearch(searchQuery)}>
                        Try Again
                      </button>
                    </div>
                  </div>
                )}

                {results && !loading && (
                  <>
                    <div className="results-actions">
                      <div className="results-summary">
                        Found {results.total} results
                      </div>
                      <button
                        className="download-excel-button"
                        onClick={handleDownloadExcel}
                        title="Download all results as Excel file"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                        </svg>
                        Download Excel
                      </button>
                    </div>
                    <SearchResults
                      documents={results.documents}
                      total={results.total}
                      query={results.query}
                      onOpenDocument={handleOpenDocument}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Document Viewer Modal */}
      <Modal
        isOpen={documentViewer.isOpen}
        onClose={handleCloseDocument}
        maxWidth="95vw"
        maxHeight="95vh"
      >
        <DocumentViewer
          documentUrl={documentViewer.documentUrl}
          pageNumber={documentViewer.pageNumber}
          onClose={handleCloseDocument}
          isModal={true}
        />
      </Modal>
    </div>
  );
}

export default App;